import * as React from 'react';

import {Button} from 'shells/button/button';
import {colors} from 'shells/colors/colors';
import {ChevronDownIcon, EyeIcon, HtmlLinkIcon} from 'shells/icon';
import {Stack} from 'shells/layout/stack/stack';
import {LoadingText} from 'shells/loading-placeholder/loading-text';
import {Popover} from 'shells/popover/popover';
import {Body} from 'shells/typography/body';

import {appHistory} from '../../../../../history';
import {addNotification} from '../../../../../notifications/index';

import {useGetContextDocumentUsage, useGetContextDocuments} from '../../graphql/hooks';

import styles from './text-chatbot-button.module.css';

type TestChatbotOption = {
    label: string;
    value: string;
    icon: React.ReactElement<any>;
    onClick: () => void;
};

function getTestChatbotOptions(
    hasContextDocumentUsage: boolean,
    contentToCopy?: string | null
): TestChatbotOption[] {
    return [
        {
            label: 'Test chatbot',
            value: 'test-chatbot',
            icon: <EyeIcon size={12} />,
            onClick: () =>
                hasContextDocumentUsage
                    ? appHistory.push('/inbox/settings/test-chatbot')
                    : appHistory.push('/inbox/settings/chat-onboarding'),
        },
        {
            label: 'Copy public link',
            value: 'copy-public-link',
            // @ts-expect-error
            icon: <HtmlLinkIcon size={12} fill={colors.grey} />,
            onClick: () => {
                addNotification('Copied!', {
                    body: 'Link copied to clipboard',
                    icon: 'check',
                });
                navigator.clipboard.writeText(contentToCopy || '');
            },
        },
    ];
}

export function TestChatbotButton() {
    const [isPopoverOpen, setIsPopoverOpen] = React.useState<boolean>(false);
    const buttonRef = React.useRef(null);

    const {contextDocumentUsage, isLoading: isLoadingContextDocumentUsage} =
        useGetContextDocumentUsage();
    const {primaryContextDocument, isLoading: isLoadingPrimaryContextDocument} =
        useGetContextDocuments(true);

    if (isLoadingContextDocumentUsage || isLoadingPrimaryContextDocument) {
        return <LoadingText height={31} width={112} />;
    }

    const hasContextDocumentUsage = Boolean(
        contextDocumentUsage && contextDocumentUsage.wordsUsage
    );

    const contentToCopy = primaryContextDocument ? primaryContextDocument.shareablePreviewUrl : '';

    return (
        <>
            <Button
                variant='secondary'
                size='big'
                getButtonRef={buttonRef}
                onClick={() => setIsPopoverOpen(true)}
            >
                <Stack isHorizontal={true} alignItems='center' spacing={8}>
                    <EyeIcon size={13} alignCenter={true} />
                    <Body size='small'>Preview</Body>
                    <ChevronDownIcon />
                </Stack>
            </Button>
            {isPopoverOpen && (
                <Popover
                    anchor={buttonRef.current}
                    location='bottom-left'
                    onBlur={() => setIsPopoverOpen(false)}
                    hideNub={true}
                >
                    <div className='flex flex-dir-col'>
                        {getTestChatbotOptions(hasContextDocumentUsage, contentToCopy).map(
                            (item) => {
                                const Icon = item.icon;

                                return (
                                    <button
                                        key={item.value}
                                        // @ts-expect-error
                                        as='button'
                                        onClick={() => {
                                            item.onClick();
                                            setIsPopoverOpen(false);
                                        }}
                                        className={styles['preview-option-button']}
                                    >
                                        {Icon}
                                        <Body size='small'>{item.label}</Body>
                                    </button>
                                );
                            }
                        )}
                    </div>
                </Popover>
            )}
        </>
    );
}
